import { useCallback, useEffect, useSyncExternalStore } from "react";
import type {
  Block,
  BuilderRevision,
  PageRecord,
  PageStatus,
  PageTemplate,
} from "@/admin/demo-data/pages";
import {
  createBuilderPageFn,
  deleteBuilderPageFn,
  deleteBuilderSectionFn,
  getBuilderPageFn,
  listBuilderPagesFn,
  listBuilderRevisionsFn,
  listBuilderSectionsFn,
  publishBuilderPageFn,
  restoreBuilderRevisionFn,
  saveBuilderDraftFn,
  saveBuilderSectionFn,
  unpublishBuilderPageFn,
} from "@/lib/builder/builder.functions";

/* ────────────────────────────────────────────────────────────
   F30.2 — Page Builder store.

   PRIMARY PERSISTENCE IS THE DATABASE (`builder_pages`,
   `builder_revisions`, `builder_sections`) through the admin
   server functions in `src/lib/builder/builder.functions.ts`.

   sessionStorage is now only a *secondary* offline mirror so the
   editor can paint instantly on a refresh while the authoritative
   rows are re-fetched. Nothing is ever seeded from demo fixtures.
   ──────────────────────────────────────────────────────────── */

export type SavedSection = {
  id: string;
  name: string;
  global: boolean;
  updated_at: string;
  blocks: Block[];
};

type StoreShape = {
  pages: PageRecord[];
  revisions: Record<string, BuilderRevision[]>;
  sections: SavedSection[];
  /** true once the DB fetch has completed at least once this session */
  hydrated: boolean;
  /** last transport/permission error, surfaced by the workspace UI */
  error: string | null;
};

const CACHE_KEY = "mah.builder.cache.v3";
const LEGACY_KEYS = ["mah.builder.v1", "mah.builder.v2"];

function emptyState(): StoreShape {
  return { pages: [], revisions: {}, sections: [], hydrated: false, error: null };
}

let state: StoreShape = emptyState();
let loadError: string | null = null;
let hydrating: Promise<void> | null = null;
const listeners = new Set<() => void>();

/* ── local mirror (secondary only) ─────────────────────────── */

function isValidShape(v: unknown): v is Pick<StoreShape, "pages" | "revisions" | "sections"> {
  if (!v || typeof v !== "object") return false;
  const s = v as Partial<StoreShape>;
  return (
    Array.isArray(s.pages) &&
    Array.isArray(s.sections) &&
    !!s.revisions &&
    typeof s.revisions === "object"
  );
}

function readMirror() {
  if (typeof window === "undefined") return;
  try {
    LEGACY_KEYS.forEach((k) => window.sessionStorage.removeItem(k));
    const raw = window.sessionStorage.getItem(CACHE_KEY);
    if (!raw) return;
    const parsed: unknown = JSON.parse(raw);
    if (isValidShape(parsed)) {
      state = {
        ...state,
        pages: parsed.pages,
        revisions: parsed.revisions,
        sections: parsed.sections,
      };
    } else {
      window.sessionStorage.removeItem(CACHE_KEY);
    }
  } catch {
    try {
      window.sessionStorage.removeItem(CACHE_KEY);
    } catch {
      /* ignore */
    }
  }
}

function writeMirror() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ pages: state.pages, revisions: state.revisions, sections: state.sections }),
    );
  } catch {
    /* quota */
  }
}

function commit(next: StoreShape) {
  state = next;
  writeMirror();
  listeners.forEach((l) => l());
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

const SERVER_SNAPSHOT: StoreShape = emptyState();

/* ── row ↔ PageRecord mapping ──────────────────────────────── */

type PageRow = {
  id: string;
  slug: string;
  title: string;
  template: string;
  locale?: string;
  status: string;
  draft_doc?: unknown;
  published_doc?: unknown;
  seo?: unknown;
  published_at?: string | null;
  updated_at: string;
};

function toStatus(s: string): PageStatus {
  return (["published", "draft", "scheduled", "private", "archived"] as const).includes(
    s as PageStatus,
  )
    ? (s as PageStatus)
    : "draft";
}

function rowToPage(row: PageRow, previous?: PageRecord): PageRecord {
  const doc = (row.draft_doc ?? {}) as { blocks?: Block[]; meta?: Record<string, unknown> };
  return {
    id: row.id,
    title: row.title,
    slug: row.slug.startsWith("/") ? row.slug : `/${row.slug}`,
    template: (row.template || "default") as PageTemplate,
    status: toStatus(row.status),
    author: (doc.meta?.author as string) ?? previous?.author ?? "—",
    updated_at: row.updated_at,
    published_at: row.published_at ?? null,
    seo: ((row.seo as PageRecord["seo"]) ??
      previous?.seo ?? {
        robotsIndex: true,
        robotsFollow: true,
        schema: "WebPage",
      }) as PageRecord["seo"],
    blocks: doc.blocks ?? previous?.blocks ?? [],
  };
}

function pageToDoc(page: PageRecord) {
  return {
    blocks: page.blocks as unknown[],
    meta: { author: page.author, template: page.template, body_class: page.body_class ?? null },
  };
}

/* ── hydration ─────────────────────────────────────────────── */

/**
 * Loads the authoritative rows from the database. Safe to call from
 * every Builder route mount — concurrent calls share one request.
 */
export function hydrateBuilderStore(force = false): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (hydrating) return hydrating;
  if (state.hydrated && !force) return Promise.resolve();

  hydrating = (async () => {
    try {
      const [pageRows, sectionRows] = await Promise.all([
        listBuilderPagesFn(),
        listBuilderSectionsFn().catch(() => []),
      ]);

      // The list endpoint omits the document; fetch each doc so the editor
      // and the card block counts reflect real persisted content.
      const docs = await Promise.all(
        (pageRows as PageRow[]).map((r) =>
          getBuilderPageFn({ data: { id: r.id } }).catch(() => null),
        ),
      );

      const pages = (pageRows as PageRow[]).map((r, i) => {
        const full = (docs[i] as PageRow | null) ?? r;
        return rowToPage(
          full,
          state.pages.find((p) => p.id === r.id),
        );
      });

      const sections: SavedSection[] = (
        sectionRows as Array<{
          id: string;
          name: string;
          is_global: boolean;
          updated_at: string;
          blocks: unknown;
        }>
      ).map((s) => ({
        id: s.id,
        name: s.name,
        global: !!s.is_global,
        updated_at: s.updated_at,
        blocks: (s.blocks ?? []) as Block[],
      }));

      loadError = null;
      commit({ ...state, pages, sections, hydrated: true, error: null });
    } catch (err) {
      loadError = err instanceof Error ? err.message : String(err);
      commit({ ...state, hydrated: true, error: loadError });
    } finally {
      hydrating = null;
    }
  })();

  return hydrating;
}

if (typeof window !== "undefined") readMirror();

/** Last non-fatal error raised while talking to the Builder backend. */
export function builderCacheError(): string | null {
  return loadError;
}

/** Drop the local mirror and re-read everything from the database. */
export function resetBuilderCache() {
  if (typeof window !== "undefined") {
    try {
      window.sessionStorage.removeItem(CACHE_KEY);
      LEGACY_KEYS.forEach((k) => window.sessionStorage.removeItem(k));
    } catch {
      /* ignore */
    }
  }
  loadError = null;
  commit(emptyState());
  void hydrateBuilderStore(true);
}

export function useBuilderStore<T>(select: (s: StoreShape) => T): T {
  // Authoritative read from `builder_pages` / `builder_sections`.
  useEffect(() => {
    void hydrateBuilderStore();
  }, []);
  const get = useCallback(() => select(state), [select]);
  const getServer = useCallback(() => select(SERVER_SNAPSHOT), [select]);
  return useSyncExternalStore(subscribe, get, getServer);
}

/* ── pages ─────────────────────────────────────────────────── */

export function listBuilderPages(): PageRecord[] {
  return state.pages;
}

export function getBuilderPage(id: string): PageRecord | undefined {
  return state.pages.find((p) => p.id === id);
}

function newUuid() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/**
 * Optimistic local write + authoritative DB write-through.
 * Callers stay synchronous; failures surface as store errors.
 */
export function saveBuilderPage(page: PageRecord) {
  const exists = state.pages.some((p) => p.id === page.id);
  commit({
    ...state,
    pages: exists ? state.pages.map((p) => (p.id === page.id ? page : p)) : [page, ...state.pages],
  });

  void saveBuilderDraftFn({
    data: {
      id: page.id,
      doc: pageToDoc(page),
      title: page.title,
      seo: (page.seo ?? {}) as Record<string, unknown>,
      snapshot: false,
    },
  }).catch((err: unknown) => {
    loadError = err instanceof Error ? err.message : String(err);
    commit({ ...state, error: loadError });
  });
}

export function createBuilderPage(title: string, slug: string): PageRecord {
  const id = newUuid();
  const clean = slug.replace(/^\/+|\/+$/g, "") || `page-${id.slice(0, 6)}`;
  const page: PageRecord = {
    id,
    title,
    slug: `/${clean}`,
    template: "default",
    status: "draft",
    author: "شما",
    updated_at: new Date().toISOString(),
    seo: { robotsIndex: true, robotsFollow: true, schema: "WebPage" },
    blocks: [],
  };
  commit({ ...state, pages: [page, ...state.pages] });

  void createBuilderPageFn({
    data: { id, title, slug: clean, template: "default", locale: "fa" },
  }).catch((err: unknown) => {
    loadError = err instanceof Error ? err.message : String(err);
    commit({ ...state, error: loadError, pages: state.pages.filter((p) => p.id !== id) });
  });

  return page;
}

export function deleteBuilderPage(id: string) {
  commit({ ...state, pages: state.pages.filter((p) => p.id !== id) });
  void deleteBuilderPageFn({ data: { id } }).catch((err: unknown) => {
    loadError = err instanceof Error ? err.message : String(err);
    commit({ ...state, error: loadError });
  });
}

/** Copies the current draft document into the public published document. */
export async function publishBuilderPage(id: string) {
  const page = state.pages.find((p) => p.id === id);
  if (page) {
    // flush the newest draft before promoting it
    await saveBuilderDraftFn({
      data: {
        id,
        doc: pageToDoc(page),
        title: page.title,
        seo: (page.seo ?? {}) as Record<string, unknown>,
        snapshot: false,
      },
    });
  }
  const res = (await publishBuilderPageFn({ data: { id } })) as { published_at: string | null };
  commit({
    ...state,
    pages: state.pages.map((p) =>
      p.id === id ? { ...p, status: "published", published_at: res.published_at ?? null } : p,
    ),
  });
  await loadRevisions(id);
  return res;
}

export async function unpublishBuilderPage(id: string) {
  await unpublishBuilderPageFn({ data: { id } });
  commit({
    ...state,
    pages: state.pages.map((p) =>
      p.id === id ? { ...p, status: "draft", published_at: null } : p,
    ),
  });
}

/* ── revisions ─────────────────────────────────────────────── */

export function listRevisions(pageId: string): BuilderRevision[] {
  return state.revisions[pageId] ?? [];
}

/** Pulls the revision index for a page from `builder_revisions`. */
export async function loadRevisions(pageId: string) {
  try {
    const rows = (await listBuilderRevisionsFn({ data: { pageId } })) as Array<{
      id: string;
      label: string | null;
      created_at: string;
    }>;
    const list: BuilderRevision[] = rows.map((r) => ({
      id: r.id,
      at: r.created_at,
      label: r.label ?? "نسخه",
      blocks: [],
    }));
    commit({ ...state, revisions: { ...state.revisions, [pageId]: list } });
    return list;
  } catch {
    return state.revisions[pageId] ?? [];
  }
}

/** Persists a labelled snapshot alongside the draft save. */
export async function addRevision(pageId: string, label: string, blocks: Block[]) {
  const page = state.pages.find((p) => p.id === pageId);
  await saveBuilderDraftFn({
    data: {
      id: pageId,
      doc: page
        ? { ...pageToDoc(page), blocks: blocks as unknown[] }
        : { blocks: blocks as unknown[] },
      snapshot: true,
      label,
    },
  });
  return loadRevisions(pageId);
}

export async function restoreRevision(pageId: string, revisionId: string) {
  const res = (await restoreBuilderRevisionFn({ data: { pageId, revisionId } })) as {
    doc: { blocks?: Block[] };
  };
  const blocks = res.doc?.blocks ?? [];
  commit({
    ...state,
    pages: state.pages.map((p) => (p.id === pageId ? { ...p, blocks } : p)),
  });
  return blocks;
}

export function deleteRevision(pageId: string, revId: string) {
  // Revisions are immutable audit rows; only the local index is pruned.
  const list = (state.revisions[pageId] ?? []).filter((r) => r.id !== revId);
  commit({ ...state, revisions: { ...state.revisions, [pageId]: list } });
}

export function renameRevision(pageId: string, revId: string, label: string) {
  const list = (state.revisions[pageId] ?? []).map((r) => (r.id === revId ? { ...r, label } : r));
  commit({ ...state, revisions: { ...state.revisions, [pageId]: list } });
}

/* ── saved / global sections ───────────────────────────────── */

export function listSections(): SavedSection[] {
  return state.sections;
}

export function saveSection(name: string, blocks: Block[], global = false) {
  const sec: SavedSection = {
    id: newUuid(),
    name,
    global,
    updated_at: new Date().toISOString(),
    blocks: JSON.parse(JSON.stringify(blocks)) as Block[],
  };
  commit({ ...state, sections: [sec, ...state.sections] });

  void saveBuilderSectionFn({
    data: {
      name,
      kind: "section",
      is_global: global,
      is_published: global,
      blocks: blocks as unknown[],
      settings: {},
    },
  })
    .then((saved) => {
      const row = saved as { id: string } | null;
      if (!row) return;
      commit({
        ...state,
        sections: state.sections.map((s) => (s.id === sec.id ? { ...s, id: row.id } : s)),
      });
    })
    .catch((err: unknown) => {
      loadError = err instanceof Error ? err.message : String(err);
      commit({ ...state, error: loadError });
    });

  return sec;
}

export function deleteSection(id: string) {
  commit({ ...state, sections: state.sections.filter((x) => x.id !== id) });
  void deleteBuilderSectionFn({ data: { id } }).catch(() => undefined);
}
